/**
 * 用户风险画像组件
 *
 * 按用户ID查询风险画像，展示违规统计、触发词分布、行为时间线。
 */

import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  safeguardAdminApi,
  type UserProfile,
} from '../../services/safeguardAdminApi';
import { StatusBadge } from './RiskDashboard';

export const UserProfilePanel: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    const id = parseInt(userId, 10);
    if (isNaN(id) || id <= 0) {
      setError('请输入有效的用户ID');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const result = await safeguardAdminApi.getUserProfile(id);
      setProfile(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '查询失败');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 搜索栏 */}
      <div className="flex gap-3 items-center">
        <input
          type="number"
          value={userId}
          onChange={e => setUserId(e.target.value)}
          placeholder="输入用户ID"
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-48"
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
        />
        <button
          onClick={handleSearch}
          disabled={loading}
          className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? '查询中...' : '查询画像'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
      )}

      {profile && (
        <div className="space-y-4">
          {/* 基本信息 */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">基本信息</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <div className="text-xs text-gray-400">用户ID</div>
                <div className="text-sm font-medium">{profile.user_id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">邮箱</div>
                <div className="text-sm font-medium">{profile.email}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">昵称</div>
                <div className="text-sm font-medium">{profile.nickname || '-'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400">状态</div>
                <StatusBadge status={profile.status} />
              </div>
              {profile.status === 'banned' && (
                <>
                  <div>
                    <div className="text-xs text-gray-400">封禁原因</div>
                    <div className="text-sm text-red-600">{profile.banned_reason || '-'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400">封禁至</div>
                    <div className="text-sm text-red-600">
                      {profile.banned_until ? new Date(profile.banned_until).toLocaleString('zh-CN') : '永久'}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* 违规统计 */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-400">审核总数</div>
              <div className="text-lg font-bold text-gray-700">{profile.total_checks}</div>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-100 p-3">
              <div className="text-xs text-gray-400">被拦截</div>
              <div className="text-lg font-bold text-red-600">{profile.blocked_count}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg border border-yellow-100 p-3">
              <div className="text-xs text-gray-400">被警告</div>
              <div className="text-lg font-bold text-yellow-600">{profile.warned_count}</div>
            </div>
            <div className="bg-orange-50 rounded-lg border border-orange-100 p-3">
              <div className="text-xs text-gray-400">高危次数</div>
              <div className="text-lg font-bold text-orange-600">{profile.high_severity_count}</div>
            </div>
            <div className="bg-purple-50 rounded-lg border border-purple-100 p-3">
              <div className="text-xs text-gray-400">违规计数</div>
              <div className="text-lg font-bold text-purple-600">{profile.violation_count}</div>
            </div>
          </div>

          {/* 触发词分布 */}
          {profile.category_distribution.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">词库分类分布</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={profile.category_distribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="category" type="category" width={80} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="次数" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* 违规时间线 */}
          {profile.violation_timeline.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">违规时间线（最近20条）</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {profile.violation_timeline.map((v, idx) => (
                  <div key={idx} className="flex items-start gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-400 whitespace-nowrap w-36 flex-shrink-0">
                      {v.created_at ? new Date(v.created_at).toLocaleString('zh-CN') : '-'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                      v.action === 'block' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {v.action === 'block' ? '拦截' : '警告'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {v.source_endpoint || '-'} | {v.check_direction === 'input' ? '输入' : '输出'}
                    </span>
                    <span className="text-xs text-gray-600 flex-1 truncate">
                      {v.matched_words.length > 0 ? v.matched_words.join(', ') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 封禁历史 */}
          {profile.ban_history.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">封禁历史</h3>
              <div className="space-y-2">
                {profile.ban_history.map((b, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {b.created_at ? new Date(b.created_at).toLocaleString('zh-CN') : '-'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      b.action_type === 'ban'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {b.action_type === 'ban' ? '封禁' : '解封'}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      b.ban_type === 'auto'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {b.ban_type === 'auto' ? '自动' : '手动'}
                    </span>
                    <span className="text-xs text-gray-600">
                      {b.duration_hours > 0 ? `${b.duration_hours}小时` : '永久'}
                    </span>
                    <span className="text-xs text-gray-500 flex-1 truncate">{b.reason || '-'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
